/**
 * DrillDownModal Component - Modal container for drill-down views
 *
 * Features:
 * - Breadcrumb navigation
 * - Back/Reset buttons
 * - Keyboard navigation (Escape, Backspace)
 * - Smooth transitions (300ms)
 * - Loading and error states
 * - Export functionality
 * - Dynamic content rendering based on drill-down level
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  X,
  ArrowLeft,
  Home,
  Download,
  Share2,
  Loader,
  AlertCircle,
  ChevronRight,
  FileSpreadsheet,
  FileText,
} from 'lucide-react';
import { useDrillDown } from '../contexts/DrillDownContext';
import { Breadcrumbs, CompactBreadcrumbs } from './Breadcrumbs';
import '../styles/drill-down.css';
import { logger } from '../utils/logger';
import { toast } from '../utils/toast';

// Import detail view components
import ViolationCategoryDetail from './drill-down/ViolationCategoryDetail';
import MonthDetailView from './drill-down/MonthDetailView';
import StateDetailView from './drill-down/StateDetailView';
import CarrierNetworkDetail from './drill-down/CarrierNetworkDetail';
import CrashSeverityDetailView from './drill-down/CrashSeverityDetailView';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

interface ExportData {
  title: string;
  level: string;
  items: Array<Record<string, unknown>>;
  filters?: Record<string, unknown>;
  generatedAt: string;
}

interface DrillDownData {
  type?: 'violation' | 'crash' | 'inspection' | 'carrier';
  title?: string;
  items?: Record<string, unknown>[];
  violations?: Record<string, unknown>[];
  data?: Record<string, unknown>[];
  metadata?: Record<string, unknown>;
  [key: string]: unknown;
}

export interface DrillDownModalProps {
  onExportCSV?: (data: ExportData) => void;
  onExportPDF?: (data: ExportData) => void;
  title?: string;
  className?: string;
}

// ============================================================================
// COMPONENT
// ============================================================================

export const DrillDownModal: React.FC<DrillDownModalProps> = ({
  onExportCSV,
  onExportPDF,
  title,
  className = '',
}) => {
  const {
    isOpen,
    isLoading,
    error,
    breadcrumbs,
    currentView,
    currentData,
    goBack,
    goToLevel,
    reset,
    closeDrillDown,
    setError,
    getShareableUrl,
  } = useDrillDown();

  const modalRef = useRef<HTMLDivElement>(null);
  const [showShareTooltip, setShowShareTooltip] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  // Handle animation state
  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      const timer = setTimeout(() => setIsAnimating(false), 300);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [isOpen]);

  // Focus trap for accessibility
  useEffect(() => {
    if (isOpen && modalRef.current) {
      const focusableElements = modalRef.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      const firstElement = focusableElements[0] as HTMLElement;
      const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

      const handleTab = (e: KeyboardEvent) => {
        if (e.key !== 'Tab') return;

        if (e.shiftKey) {
          if (document.activeElement === firstElement) {
            e.preventDefault();
            lastElement?.focus();
          }
        } else {
          if (document.activeElement === lastElement) {
            e.preventDefault();
            firstElement?.focus();
          }
        }
      };

      document.addEventListener('keydown', handleTab);
      firstElement?.focus();

      return () => document.removeEventListener('keydown', handleTab);
    }
    return undefined;
  }, [isOpen]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Handle overlay click
  const handleOverlayClick = useCallback((e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      closeDrillDown();
    }
  }, [closeDrillDown]);

  // Handle share
  const handleShare = useCallback(async () => {
    const url = getShareableUrl();

    try {
      await navigator.clipboard.writeText(url);
      setShowShareTooltip(true);
      setTimeout(() => setShowShareTooltip(false), 2000);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Copy failed';
      logger.warn('Failed to copy URL:', { error: err });
    }
  }, [getShareableUrl]);

  // Handle CSV export
  const handleExportCSV = useCallback(() => {
    if (onExportCSV && currentData) {
      onExportCSV(currentData as unknown as ExportData);
    } else {
      // Default CSV export logic
      try {
        const dataToExport = currentData?.items || currentData?.violations || currentData?.data || [currentData];
        if (!Array.isArray(dataToExport) || dataToExport.length === 0) {
          logger.debug('Export attempted with no data');
          toast.info('No data available to export');
          return;
        }

        const headers = Object.keys(dataToExport[0]).join(',');
        const rows = dataToExport.map(item =>
          Object.values(item).map(val =>
            typeof val === 'string' && val.includes(',') ? `"${val}"` : val
          ).join(',')
        );
        const csv = [headers, ...rows].join('\n');

        const blob = new Blob([csv], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `drill-down-${currentView}-${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Export failed';
        logger.error('Export failed:', { error: err, format: 'csv' });
      }
    }
  }, [currentData, currentView, onExportCSV]);

  // Handle PDF export
  const handleExportPDF = useCallback(() => {
    if (onExportPDF && currentData) {
      onExportPDF(currentData as unknown as ExportData);
    } else {
      // Trigger print dialog as fallback
      window.print();
    }
  }, [currentData, onExportPDF]);

  // Get current breadcrumb label
  const currentLabel = breadcrumbs[breadcrumbs.length - 1]?.label || currentView || 'Detail View';

  // Render the appropriate detail view based on currentView
  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="drill-down-loading">
          <div className="drill-down-loading-spinner" />
          <p className="drill-down-loading-text">Loading data...</p>
        </div>
      );
    }

    if (error) {
      return (
        <div className="drill-down-error">
          <AlertCircle size={48} className="drill-down-error-icon" />
          <p className="drill-down-error-message">{error}</p>
          <button
            className="drill-down-error-btn"
            onClick={() => setError(null)}
          >
            Try Again
          </button>
        </div>
      );
    }

    // Route to appropriate detail view component
    switch (currentView) {
      case 'violation-category':
      case 'category-detail':
      case 'basic-category':
        return <ViolationCategoryDetail data={currentData} />;

      case 'month':
      case 'month-detail':
      case 'month-category':
        return <MonthDetailView data={currentData} />;

      case 'state':
      case 'state-detail':
      case 'county':
        return <StateDetailView data={currentData} />;

      case 'carrier':
      case 'carrier-network':
      case 'carrier-detail':
        return <CarrierNetworkDetail data={currentData} />;

      case 'crash-severity':
      case 'severity-detail':
      case 'fatal':
      case 'injury':
      case 'towaway':
        return <CrashSeverityDetailView data={currentData} />;

      default:
        // Generic detail view for unknown types
        return (
          <div className="drill-down-generic-view">
            <div className="drill-down-stats-row">
              {currentData && typeof currentData === 'object' && (
                Object.entries(currentData).map(([key, value]) => {
                  if (typeof value === 'number' || typeof value === 'string') {
                    return (
                      <div key={key} className="drill-down-stat-item">
                        <div className="drill-down-stat-value">
                          {typeof value === 'number' ? value.toLocaleString() : value}
                        </div>
                        <div className="drill-down-stat-label">
                          {key.replace(/_/g, ' ').replace(/([A-Z])/g, ' $1').trim()}
                        </div>
                      </div>
                    );
                  }
                  return null;
                })
              )}
            </div>

            {currentData?.items && Array.isArray(currentData.items) && (
              <div className="drill-down-detail-card" style={{ marginTop: '1.5rem' }}>
                <div className="drill-down-detail-card-header">
                  <h4 className="drill-down-detail-card-title">
                    Data Items ({currentData.items.length})
                  </h4>
                </div>
                <div style={{ maxHeight: '400px', overflow: 'auto' }}>
                  <table style={{ width: '100%', fontSize: '0.8125rem', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid var(--color-border)' }}>
                        {currentData.items[0] && Object.keys(currentData.items[0]).slice(0, 6).map(key => (
                          <th key={key} style={{ padding: '0.75rem', textAlign: 'left', fontWeight: 600 }}>
                            {key.replace(/_/g, ' ')}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {currentData.items.slice(0, 50).map((item: Record<string, unknown>, idx: number) => (
                        <tr key={idx} style={{ borderBottom: '1px solid var(--color-border)' }}>
                          {Object.values(item).slice(0, 6).map((val: unknown, vidx: number) => (
                            <td key={vidx} style={{ padding: '0.75rem' }}>
                              {typeof val === 'number' ? (val as number).toLocaleString() : String(val || '-')}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        );
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className={`drill-down-overlay ${isOpen ? 'open' : ''} ${className}`}
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="drill-down-title"
    >
      <div
        className="drill-down-modal"
        ref={modalRef}
        role="document"
      >
        {/* Header */}
        <div className="drill-down-header">
          <div className="drill-down-header-left">
            <h2 id="drill-down-title" className="drill-down-title">
              {title || currentLabel}
            </h2>
            <p className="drill-down-subtitle">
              Click elements to drill deeper, use breadcrumbs to navigate
            </p>
          </div>
          <div className="drill-down-header-actions">
            <button
              className="drill-down-close-btn"
              onClick={closeDrillDown}
              aria-label="Close modal (Escape)"
              title="Close (Esc)"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Navigation Bar */}
        <div className="drill-down-nav">
          <div className="drill-down-nav-left">
            <button
              className="drill-down-nav-btn"
              onClick={goBack}
              disabled={breadcrumbs.length <= 1}
              title="Go back (Backspace)"
            >
              <ArrowLeft size={16} />
              Back
              <span className="keyboard-hint">Backspace</span>
            </button>
            <button
              className="drill-down-nav-btn"
              onClick={reset}
              disabled={breadcrumbs.length <= 1}
              title="Reset to overview"
            >
              <Home size={16} />
              Reset
            </button>

            {/* Breadcrumbs */}
            <Breadcrumbs
              crumbs={breadcrumbs.map((b, i) => ({
                id: b.id,
                label: b.label,
                level: b.level,
                onClick: i < breadcrumbs.length - 1 ? () => goToLevel(i) : undefined,
              }))}
              showHomeIcon={true}
              style={{ marginLeft: '1rem' }}
            />
          </div>

          <div className="drill-down-nav-right">
            <button
              className="drill-down-nav-btn"
              onClick={handleShare}
              title="Copy shareable link"
              style={{ position: 'relative' }}
            >
              <Share2 size={16} />
              Share
              {showShareTooltip && (
                <span style={{
                  position: 'absolute',
                  bottom: '100%',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  padding: '0.25rem 0.5rem',
                  backgroundColor: 'var(--color-success)',
                  color: 'white',
                  fontSize: '0.75rem',
                  borderRadius: '4px',
                  whiteSpace: 'nowrap',
                  marginBottom: '4px',
                }}>
                  Link copied!
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Content Area */}
        <div className={`drill-down-content ${isAnimating ? 'drill-down-view-enter-active' : ''}`}>
          {renderContent()}
        </div>

        {/* Export Section */}
        <div className="drill-down-export-section">
          <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-muted)', marginRight: 'auto' }}>
            Export this view:
          </span>
          <button className="drill-down-export-btn" onClick={handleExportCSV}>
            <FileSpreadsheet />
            CSV
          </button>
          <button className="drill-down-export-btn" onClick={handleExportPDF}>
            <FileText />
            PDF
          </button>
        </div>
      </div>
    </div>
  );
};

export default DrillDownModal;
